import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, ExternalLink, CheckCircle2 } from "lucide-react";
import { api, formatApiError, type PublicReview } from "@/lib/api";

export default function ReviewPage() {
  const { token = "" } = useParams();
  const [data, setData] = useState<PublicReview | null>(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { api.publicReviewGet(token).then(setData).catch((e) => setError(formatApiError(e))); }, [token]);

  const submit = async () => {
    if (!rating) return;
    try { await api.publicReviewFeedback(token, rating, comment); setDone(true); } catch (e) { setError(formatApiError(e)); }
  };
  const openGoogle = async () => {
    try { const r = await api.publicReviewClick(token); if (r.google_review_url) window.open(r.google_review_url, "_blank"); }
    catch (e) { setError(formatApiError(e)); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10" data-testid="review-page">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-lg">
        {error && !data ? <p className="text-slate-700">{error}</p> : !data ? <p className="text-slate-400">Loading…</p> : (
          <>
            <h1 className="font-display text-2xl font-semibold text-slate-900">How did we do?</h1>
            <p className="mt-1 text-sm text-slate-500">{data.business_name} would love your feedback.</p>
            {done ? (
              <div className="mt-6" data-testid="review-thanks">
                <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" />
                <p className="mt-2 text-slate-700">Thank you for your feedback!</p>
                {data.google_review_url && <button onClick={openGoogle} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white" data-testid="review-google-btn"><ExternalLink className="h-4 w-4" /> Leave a Google review</button>}
              </div>
            ) : (
              <>
                <div className="mt-5 flex justify-center gap-1" data-testid="review-stars">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => setRating(n)} data-testid={`review-star-${n}`}>
                      <Star className={`h-9 w-9 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                    </button>
                  ))}
                </div>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Tell us more (optional)" data-testid="review-comment"
                  className="mt-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                <button onClick={submit} disabled={!rating} data-testid="review-submit-btn"
                  className="mt-3 w-full rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Submit feedback</button>
                {data.google_review_url && <button onClick={openGoogle} className="mt-3 inline-flex items-center gap-2 text-sm text-blue-600" data-testid="review-google-link"><ExternalLink className="h-4 w-4" /> Or leave a Google review</button>}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
